import { Controller } from "@hotwired/stimulus";
import { useIntersection } from "stimulus-use";
import URL from "hb/lib/url";

export default class extends Controller {
  static targets = ["input", "list", "item", "noResults"];
  static values = {
    placeholder: String,
    startingFilter: String,
    autoFocus: { type: Boolean, default: false },
  };

  connect() {
    this.selectedIndex = -1;
    this.allItems = Array.from(this.itemTargets);
    this.visibleItems = [...this.allItems];

    if (this.autoFocusValue) {
      useIntersection(this, { threshold: 0.1 });
    }

    if (this.hasStartingFilterValue) {
      this.inputTarget.value = this.startingFilterValue;
      this.filterItems(this.startingFilterValue.toLowerCase().trim());
    }
  }

  appear() {
    setTimeout(() => {
      this.inputTarget.focus();
    }, 50);
  }

  disappear() {
    this.inputTarget.value = "";
    this.showAllItems();
  }

  search(event) {
    const query = event.target.value.toLowerCase().trim();

    if (query === "") {
      this.showAllItems();
    } else {
      this.filterItems(query);
    }

    this.selectedIndex = -1;
    this.updateSelection();
  }

  navigate(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.moveDown();
        break;
      case "ArrowUp":
        event.preventDefault();
        this.moveUp();
        break;
      case "Enter":
        event.preventDefault();
        this.selectCurrent();
        break;
      case "Escape":
        event.preventDefault();
        this.close();
        break;
    }
  }

  selectItem(event) {
    const item = event.currentTarget;
    this.handleSelection(item);
  }

  moveDown() {
    if (this.visibleItems.length === 0) return;

    this.selectedIndex = (this.selectedIndex + 1) % this.visibleItems.length;
    this.updateSelection();
  }

  moveUp() {
    if (this.visibleItems.length === 0) return;

    this.selectedIndex =
      this.selectedIndex <= 0
        ? this.visibleItems.length - 1
        : this.selectedIndex - 1;
    this.updateSelection();
  }

  selectCurrent() {
    if (this.selectedIndex >= 0 && this.visibleItems[this.selectedIndex]) {
      this.handleSelection(this.visibleItems[this.selectedIndex]);
    }
  }

  updateSelection() {
    this.visibleItems.forEach((item, index) => {
      item.classList.toggle("selected", index === this.selectedIndex);
    });

    if (this.selectedIndex >= 0 && this.visibleItems[this.selectedIndex]) {
      this.visibleItems[this.selectedIndex].scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }

  filterItems(query) {
    const regexpQuery = new RegExp(query, "i");

    this.visibleItems = this.allItems.filter((item) => {
      const tokens = item.dataset.tokens
        ? (Array.isArray(item.dataset.tokens)
            ? item.dataset.tokens
            : item.dataset.tokens.split(",")
          ).map((token) => token.trim().toLowerCase())
        : [];

      const matches = tokens.some((token) => regexpQuery.test(token));

      item.classList.toggle("d-none", !matches);

      return matches;
    });

    // Hide/show group headers based on visible items
    this.updateGroupVisibility();
    this.updateNoResultsVisibility();
  }

  showAllItems() {
    this.allItems.forEach((item) => {
      item.classList.remove("d-none");
    });
    this.visibleItems = [...this.allItems];
    this.updateGroupVisibility();
    this.updateNoResultsVisibility();
  }

  updateGroupVisibility() {
    const groups = this.listTarget.querySelectorAll("[data-group]");

    groups.forEach((group) => {
      const groupItems = group.querySelectorAll(
        '[data-searchable-list-target="item"]',
      );
      const hasVisibleItems = Array.from(groupItems).some(
        (item) => !item.classList.contains("d-none"),
      );
      group.style.display = hasVisibleItems ? "" : "none";
    });
  }

  updateNoResultsVisibility() {
    if (this.hasNoResultsTarget) {
      const hasVisibleItems = this.visibleItems.length > 0;
      this.noResultsTarget.classList.toggle("d-none", hasVisibleItems);
    }
  }

  handleSelection(item) {
    // If it's an anchor tag, navigate to the href
    if (item.tagName === "A" && item.href) {
      URL.turboHandleMethod(item);
      return;
    }

    const value = item.dataset.value;
    const id = item.dataset.id || item.textContent.trim();

    this.dispatch("select", {
      detail: {
        value: value,
        id: id,
        element: item,
        data: item.dataset,
      },
    });
  }

  close() {
    this.dispatch("close");
  }

  clear() {
    this.inputTarget.value = "";
    this.showAllItems();
    this.selectedIndex = -1;
    this.updateSelection();
  }

  focus() {
    this.inputTarget.focus();
  }
}
